import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Heart, Clock, RefreshCw, Wifi, WifiOff, Activity, ChevronRight, ClipboardList, Plus, Share2, Settings, AlertCircle, Volume2, VolumeX } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../store/authStore';
import { useDeviceWebSocket } from '../hooks/useDeviceWebSocket';
import { useHeartbeatSound } from '../hooks/useHeartbeatSound';
import { getSessions } from '../api/sessions';
import { getMyDevices } from '../api/devices';
import { SkeletonBlock } from '../components/SkeletonBlock';
export function Dashboard() {
  const navigate = useNavigate();
  const {
    user,
    token
  } = useAuth();
  const {
    isConnected,
    isBeltOnline,
    bpm,
    piezoValue,
    waveformData
  } = useDeviceWebSocket(token);
  const {
    soundEnabled,
    toggleSound
  } = useHeartbeatSound(bpm, isConnected);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  const [device, setDevice] = useState<any>(null);
  const [stats, setStats] = useState({
    avgBpm: 0,
    timeMonitored: 0
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  // Fetch Data
  const fetchData = async () => {
    if (!token) return;
    setError(false);
    try {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const [sessionsRes, devicesRes] = await Promise.all([getSessions(token, {
        from: startOfToday.toISOString()
      }), getMyDevices(token)]);
      setSessions(sessionsRes || []);
      if (devicesRes && devicesRes.length > 0) {
        setDevice(devicesRes[0]);
      }
      // Compute stats
      if (sessionsRes && sessionsRes.length > 0) {
        let totalDuration = 0;
        let totalBpm = 0;
        let bpmCount = 0;
        sessionsRes.forEach((s: any) => {
          if (s.duration) totalDuration += s.duration;
          if (s.avgBpm) {
            totalBpm += s.avgBpm;
            bpmCount++;
          }
        });
        setStats({
          avgBpm: bpmCount > 0 ? Math.round(totalBpm / bpmCount) : 0,
          timeMonitored: totalDuration
        });
      }
    } catch (err) {
      // console.error('Failed to load dashboard data', err)
      setError(true);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [token]);
  // Pull to refresh
  const touchStartY = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    if (window.scrollY === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (window.scrollY === 0 && touchStartY.current > 0) {
      const touchEndY = e.changedTouches[0].clientY;
      if (touchEndY - touchStartY.current > 60) {
        setIsRefreshing(true);
        fetchData();
      }
    }
    touchStartY.current = 0;
  };
  // Canvas Waveform Rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const draw = () => {
      const width = canvas.width;
      const height = canvas.height;
      ctx.clearRect(0, 0, width, height);
      if (!isConnected) {
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(0, height / 2);
        ctx.lineTo(width, height / 2);
        ctx.strokeStyle = '#8A8A8A';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#8A8A8A';
        ctx.font = '14px "DM Sans"';
        ctx.textAlign = 'center';
        ctx.fillText('Waiting for signal...', width / 2, height / 2 - 15);
        animationRef.current = requestAnimationFrame(draw);
        return;
      }
      const data = waveformData;
      const step = width / (data.length - 1);
      // Draw Glow
      ctx.beginPath();
      for (let i = 0; i < data.length; i++) {
        const x = i * step;
        // Map 0-4095 to canvas height (invert Y so higher values are up)
        const y = height - data[i] / 4095 * height;
        if (i === 0) ctx.moveTo(x, y);else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = 'rgba(107, 33, 232, 0.3)';
      ctx.lineWidth = 6;
      ctx.lineJoin = 'round';
      ctx.stroke();
      // Draw Main Line
      ctx.beginPath();
      for (let i = 0; i < data.length; i++) {
        const x = i * step;
        const y = height - data[i] / 4095 * height;
        if (i === 0) ctx.moveTo(x, y);else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = '#6B21E8';
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.stroke();
      animationRef.current = requestAnimationFrame(draw);
    };
    // Handle resize
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    draw();
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isConnected, waveformData]);
  // Helpers
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };
  const getFirstName = () => {
    if (!user?.fullName) return 'Mama';
    return user.fullName.split(' ')[0];
  };
  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };
  const formatDuration = (minutes: number) => {
    if (!minutes) return '0h 0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };
  const getRelativeTime = (dateString: string) => {
    if (!dateString) return '--';
    const diff = Date.now() - new Date(dateString).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    return 'Yesterday';
  };
  const getBpmStatus = (currentBpm: number | null) => {
    if (!isConnected || !currentBpm) return {
      label: 'No signal',
      classes: 'bg-border text-text-secondary'
    };
    if (currentBpm < 110) return {
      label: 'Below normal',
      classes: 'bg-danger/15 text-danger'
    };
    if (currentBpm > 160) return {
      label: 'Above normal',
      classes: 'bg-danger/15 text-danger'
    };
    return {
      label: 'Normal range',
      classes: 'bg-accent/20 text-accent'
    };
  };
  const bpmStatus = getBpmStatus(bpm);
  return <main className="w-full h-full flex flex-col" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-id="element-861" data-mp="3anwtwu92lavy">
      {isRefreshing && <div className="flex justify-center py-2" data-id="element-862" data-mp="1v3e13r8kmywc">
          <RefreshCw size={20} className="text-primary animate-spin" data-id="element-863" data-mp="1v2ulih6e8mlh" />
        </div>}

      {error && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-6 flex items-center justify-between" data-id="element-864" data-mp="1gcnaunmmo7vc">
          <div className="flex items-center text-danger" data-id="element-865" data-mp="17e30c9r814i7">
            <AlertCircle size={18} className="mr-2" data-id="element-866" data-mp="17ci6yngn7rlx" />
            <span className="text-[14px] font-medium" data-id="element-867" data-mp="1bg6dd2tjgh5l">
              Some data could not be loaded. Pull down to refresh.
            </span>
          </div>
          <button onClick={fetchData} className="text-danger text-[14px] font-semibold hover:underline" data-id="element-868" data-mp="xhq9rkqz36ro">
            Retry
          </button>
        </div>}

      {/* Desktop Grid Layout */}
      <div className="flex flex-col xl:grid xl:grid-cols-[1fr_380px] xl:gap-8" data-id="element-869" data-mp="2wsrby8rzmif6">
        {/* LEFT COLUMN */}
        <div className="flex flex-col space-y-6 xl:space-y-8" data-id="element-870" data-mp="1jmocajpgjnx1">
          {/* Header */}
          <div className="flex justify-between items-start" data-id="element-871" data-mp="1fktcjjn6dild">
            <div data-id="element-872" data-mp="1flcswvx6fyxa">
              <h1 className="text-[22px] font-semibold text-text-primary" data-id="element-873" data-mp="1fmxehw1ik7j0">
                {getGreeting()}, {getFirstName()}
              </h1>
              <p className="text-[14px] text-text-secondary mt-1" data-id="element-874" data-mp="15tox6itl7vq0">
                {formatDate()}
              </p>
            </div>
            <div className="flex items-center space-x-4 xl:hidden" data-id="element-875" data-mp="10wyju3963e8a">
              <button onClick={() => navigate('/app/alerts')} className="relative p-2" aria-label="View alerts" data-id="element-876" data-mp="vviuw03vcnkm">
                <Bell size={22} className="text-text-primary" data-id="element-877" data-mp="mwfbsw4pc864" />
                {/* Alert badge would go here if we had global alert state */}
              </button>
              <button onClick={() => navigate('/app/profile')} className="w-[36px] h-[36px] bg-primary rounded-full flex items-center justify-center text-white text-[14px] font-semibold" aria-label="View profile" data-id="element-878" data-mp="2m6x6yyhlufud">
                {getFirstName()[0]}
              </button>
            </div>
          </div>

          {/* Live Monitor Hero Card */}
          <div className="bg-white rounded-[24px] shadow-elevated p-5 w-full" data-id="element-879" data-mp="2ylllmva1z248">
            <div className="flex justify-between items-center mb-4" data-id="element-880" data-mp="33kexykdlsn2t">
              <div className="flex items-center gap-2 min-w-0" data-id="element-881" data-mp="25vvej66u90hm">
                <h2 className="text-[16px] font-semibold text-text-primary whitespace-nowrap" data-id="element-882" data-mp="2ateq1yc5iz5v">
                  Live Monitor
                </h2>
                <span className={`inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap ${isBeltOnline ? 'bg-success/15 text-success' : 'bg-secondary text-text-secondary'}`} role="status" aria-live="polite" data-id="element-883" data-mp="2fr6kp046kzdr">
                  <span className="relative flex h-1.5 w-1.5" data-id="element-884" data-mp="3vbcqnofz07oa">
                    {isBeltOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-75 animate-ping" data-id="element-885" data-mp="1q3wale0duurr" />}
                    <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${isBeltOnline ? 'bg-success' : 'bg-text-secondary/60'}`} data-id="element-886" data-mp="1uvns6z1k9q5r" />
                  </span>
                  {isBeltOnline ? 'Belt Connected' : 'Belt Offline'}
                </span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-887" data-mp="175jx5g2fuyqd">
                <button onClick={toggleSound} className="w-[36px] h-[36px] rounded-full border border-primary text-primary flex items-center justify-center hover:bg-primary/5 transition-colors" aria-label={soundEnabled ? 'Mute heartbeat' : 'Play heartbeat'} data-id="element-888" data-mp="1c3va3zo15pct">
                  {soundEnabled ? <Volume2 size={18} data-id="element-889" data-mp="gurlji7qfq0c" /> : <VolumeX size={18} data-id="element-890" data-mp="gtzr4apvfybn" />}
                </button>
                <button onClick={() => navigate('/app/monitor')} className="border border-primary text-primary text-[13px] font-medium px-[14px] py-[6px] rounded-pill hover:bg-primary/5 transition-colors h-[36px]" data-id="element-891" data-mp="bvy7nooj9wzn">
                  Full Screen
                </button>
              </div>
            </div>

            <div className="w-full h-[140px] xl:h-[180px] bg-background rounded-[16px] relative overflow-hidden mb-4" data-id="element-892" data-mp="3tdzj8r1qnnwf">
              <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" role="img" aria-label="Live fetal heart rate waveform" data-id="element-893" data-mp="3pcxi24o9h1rt" />
            </div>

            <div className="flex items-end justify-between" data-id="element-894" data-mp="1ikwqt790f17v">
              <div className="flex items-center space-x-6" data-id="element-895" data-mp="1iiaiv6i7ijp4">
                <div className="flex flex-col" data-id="element-896" data-mp="1dmbo916ac5g9">
                  <motion.span key={bpm} initial={{
                  scale: 1.1
                }} animate={{
                  scale: 1
                }} className="text-[48px] font-bold text-primary leading-none" aria-live="polite" aria-label={`Current BPM: ${isConnected && bpm ? bpm : 'Unknown'}`} data-id="element-897" data-mp="1djh317fg6gls">
                    {isConnected && bpm ? bpm : '--'}
                  </motion.span>
                  <span className="text-[12px] text-text-secondary mt-1" data-id="element-898" data-mp="3vq1prjgdn2gp">
                    BPM
                  </span>
                </div>

                <div className="w-[1px] h-[40px] bg-border" data-id="element-899" data-mp="3gw6niwaq8beh" />

                <div className="flex flex-col" data-id="element-900" data-mp="3lrm3puw7ije2">
                  <span className="text-[12px] text-text-secondary mb-1" data-id="element-901" data-mp="25enicydl5yda">
                    Min
                  </span>
                  <span className="text-[20px] font-semibold text-text-primary leading-none" data-id="element-902" data-mp="2ae0cm46se7pw">
                    --
                  </span>
                </div>

                <div className="flex flex-col" data-id="element-903" data-mp="shycxlqucwnb">
                  <span className="text-[12px] text-text-secondary mb-1" data-id="element-904" data-mp="nfz9kbv3pzqd">
                    Max
                  </span>
                  <span className="text-[20px] font-semibold text-text-primary leading-none" data-id="element-905" data-mp="11g0k6j164lj1">
                    --
                  </span>
                </div>
              </div>

              <div className={`px-3 py-1 rounded-pill text-[13px] font-medium ${bpmStatus.classes}`} data-id="element-906" data-mp="9qfbvkgdnsnx">
                {bpmStatus.label}
              </div>
            </div>
          </div>

          {/* Recent Sessions (Desktop Left Col, Mobile Stacked) */}
          <div className="w-full" data-id="element-907" data-mp="dq66y0cxsuy9">
            <div className="flex justify-between items-center mb-4" data-id="element-908" data-mp="dp4j0vatodz4">
              <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-909" data-mp="3m5px2qib9x93">
                Recent Sessions
              </h2>
              <button onClick={() => navigate('/app/history')} className="text-[14px] text-primary hover:underline" data-id="element-910" data-mp="3i3csi5faw5dw">
                See all
              </button>
            </div>

            {isLoading ? <div className="space-y-3" data-id="element-911" data-mp="8n6t2dqe6miy">
                {[1, 2, 3].map(i => <SkeletonBlock key={i} height="72px" data-id="element-912" data-mp="deosuygoih2c" />)}
              </div> : sessions.length > 0 ? <div className="space-y-3" data-id="element-913" data-mp="icq9zdujo6b6">
                {sessions.slice(0, 3).map((session, i) => <div key={session.id || i} onClick={() => navigate(`/app/history/${session.id}`)} className="bg-white rounded-[16px] shadow-sm p-4 flex items-center cursor-pointer hover:-translate-y-[2px] hover:shadow-card transition-all" data-id="element-914" data-mp="mda74hf4b9xb" data-mpi={i}>
                    <div className="w-[40px] h-[40px] bg-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0" data-id="element-915" data-mp="2t7fa2iyifds3">
                      <Activity size={18} className="text-white" data-id="element-916" data-mp="2qlenj3m3k7am" />
                    </div>
                    <div className="flex-1" data-id="element-917" data-mp="2lug43v5tat35">
                      <h3 className="text-[14px] font-semibold text-text-primary" data-id="element-918" data-mp="2lsljkhygd2zl">
                        {new Date(session.createdAt || Date.now()).toLocaleDateString('en-US', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short'
                  })}
                      </h3>
                      <p className="text-[13px] text-text-secondary mt-0.5" data-id="element-919" data-mp="34il36pbixpvm">
                        {formatDuration(session.duration)} · Avg{' '}
                        {session.avgBpm || '--'} BPM
                      </p>
                    </div>
                    <ChevronRight size={20} className="text-text-secondary" data-id="element-920" data-mp="1o6o5cwr3vjtg" />
                  </div>)}
              </div> : <div className="bg-secondary rounded-[16px] p-6 flex flex-col items-center justify-center text-center" data-id="element-921" data-mp="1j9fjal2u6vmx">
                <ClipboardList size={40} className="text-primary mb-3" data-id="element-922" data-mp="f9hdfuturqwa" />
                <h3 className="text-[16px] font-semibold text-text-primary mb-1" data-id="element-923" data-mp="f7d7z8gb2jtw">
                  No sessions yet
                </h3>
                <p className="text-[14px] text-text-secondary max-w-[200px]" data-id="element-924" data-mp="689ow4h524fe">
                  Start monitoring and your sessions will appear here.
                </p>
              </div>}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex flex-col space-y-6 xl:space-y-8 mt-6 xl:mt-0" data-id="element-925" data-mp="3si3vyps9pwlq">
          {/* Today's Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-2 gap-4" data-id="element-926" data-mp="3emrr3pkeepr3">
            {/* Stat 1 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-927" data-mp="34qevun97g3u8">
              <div className="w-[32px] h-[32px] bg-primary/10 rounded-full flex items-center justify-center mb-3" data-id="element-928" data-mp="34srdezb1wc6r">
                <Heart size={16} className="text-primary" data-id="element-929" data-mp="2q3q2l35jqmwa" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-930" data-mp="2m2eamqz79pkx" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-931" data-mp="301mbrpexb7rr">
                  {stats.avgBpm || '--'}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-932" data-mp="2v4vrsqzzzs12">
                Avg BPM today
              </span>
            </div>

            {/* Stat 2 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-933" data-mp="3507b7mmcpnf5">
              <div className="w-[32px] h-[32px] bg-accent/20 rounded-full flex items-center justify-center mb-3" data-id="element-934" data-mp="3isn805onsp7m">
                <Clock size={16} className="text-accent" data-id="element-935" data-mp="3dulo5ijeslv3" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-936" data-mp="3sr2yq7jrxwgl" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-937" data-mp="3nsrm5p23h7uo">
                  {formatDuration(stats.timeMonitored)}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-938" data-mp="2dd7et2hz9cqc">
                Monitored today
              </span>
            </div>

            {/* Stat 3 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-939" data-mp="2ncesxrwjdbei">
              <div className="w-[32px] h-[32px] bg-amber-500/15 rounded-full flex items-center justify-center mb-3" data-id="element-940" data-mp="2na2c5i2il731">
                <RefreshCw size={16} className="text-amber-500" data-id="element-941" data-mp="1unhjmeaayoxw" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="80%" className="mb-1" data-id="element-942" data-mp="1zmaqhnv33hp3" /> : <span className="text-[20px] font-bold text-text-primary leading-tight mb-1 truncate" data-id="element-943" data-mp="24e2i81pz7tgt">
                  {getRelativeTime(device?.lastSeenAt)}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-944" data-mp="1bwq1an8c7o7d">Last sync</span>
            </div>

            {/* Stat 4 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-945" data-mp="3p6813j3dzzy0">
              <div className={`w-[32px] h-[32px] rounded-full flex items-center justify-center mb-3 ${device ? 'bg-primary/10' : 'bg-border'}`} data-id="element-946" data-mp="3l5o6am6ad8f1">
                {device ? <Wifi size={16} className="text-primary" data-id="element-947" data-mp="3b6ou4tmbauna" /> : <WifiOff size={16} className="text-text-secondary" data-id="element-948" data-mp="3696whzdqw1ul" />}
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="70%" className="mb-1" data-id="element-949" data-mp="31gdrdaed6rhz" /> : <span className={`text-[18px] font-bold leading-tight mb-1 ${device ? 'text-accent' : 'text-danger'}`} data-id="element-950" data-mp="2xgv447z2m4bz">
                  {device ? 'Connected' : 'Offline'}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-951" data-mp="2ij2ufwg7ga5p">
                Belt status
              </span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="w-full" data-id="element-952" data-mp="2h8n3zw7csbiq">
            <h2 className="text-[16px] font-semibold text-text-primary mb-3 xl:hidden" data-id="element-953" data-mp="2cb4y6eomucey">
              Quick Actions
            </h2>
            <div className="flex overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap md:gap-3 hide-scrollbar space-x-3 md:space-x-0" data-id="element-954" data-mp="27ayziejy3pnd">
              <button onClick={() => navigate('/app/setup')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-955" data-mp="1ybvgtbooxc9e">
                <Plus size={16} className="text-primary mr-2" data-id="element-956" data-mp="1omu1zotvt0x0" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-957" data-mp="1jnhfx6au3xvh">
                  Add Device
                </span>
              </button>
              <button onClick={() => navigate('/app/history')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-958" data-mp="1fkkr2xupb5j3">
                <Share2 size={16} className="text-primary mr-2" data-id="element-959" data-mp="3r87qvo4zoknv" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-960" data-mp="3rak81z2xaqzv">
                  Share Report
                </span>
              </button>
              <button onClick={() => navigate('/app/alerts')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-961" data-mp="42yoa6m2nrxg">
                <Bell size={16} className="text-primary mr-2" data-id="element-962" data-mp="37idyb7uga1il" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-963" data-mp="3cia153revmwk">
                  View Alerts
                </span>
              </button>
              <button onClick={() => navigate('/app/profile')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-964" data-mp="2jvfeq1b1adgg">
                <Settings size={16} className="text-primary mr-2" data-id="element-965" data-mp="3s3bmgpnzgydk" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-966" data-mp="3n6uvlnpeg5ws">
                  Settings
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-967" data-mp="rjp4q61db4s">{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </main>;
}